import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useGlobalModal } from "@/shared/context/GlobalModalContext";
import { ROUTES } from "@/shared/constants";
import { useLogout } from "@/shared/hooks/useLogout";
import { useTrialExpiry } from "@/shared/hooks/useTrialExpiry";
import { isExpiredTrialRouteAllowed } from "@/shared/helper/trialExpiry.helper";

const TrialExpiryGuard = (): null => {
  const { openModal, closeModal } = useGlobalModal();
  const { logout } = useLogout();
  const { showIndicator, isExpired } = useTrialExpiry();
  const location = useLocation();
  const hasOpenedModalRef = useRef(false);

  useEffect((): void => {
    if (isExpiredTrialRouteAllowed(location.pathname)) {
      hasOpenedModalRef.current = false;
      closeModal("account-expired");
      return;
    }

    if (!showIndicator || !isExpired) {
      hasOpenedModalRef.current = false;
      return;
    }

    if (hasOpenedModalRef.current) {
      return;
    }

    hasOpenedModalRef.current = true;

    openModal({
      modalKey: "account-expired",
      title: "Account Expired",
      descriptionNode: (
        <p className="text-sm leading-6 text-center">
          Your account has expired, please contact Automation Industries for
          activation.{" "}
          <a
            href={ROUTES.header.contactSupport}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
          >
            Contact Support
          </a>
        </p>
      ),
      showCloseButton: false,
      cancelText: "",
      actionText: "OK",
      variant: "error",
      overlayClassName: "bg-background/50 backdrop-blur-[5px]",
      closeOnAction: false,
      disableOutsideClose: true,
      disableEscapeClose: true,
      onAction: async (): Promise<void> => {
        await logout();
      },
    });
  }, [
    closeModal,
    isExpired,
    location.pathname,
    logout,
    openModal,
    showIndicator,
  ]);

  return null;
};

export default TrialExpiryGuard;
